import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { userAPI, remindersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
    ArrowLeft, History, Calendar, Users, Trophy, 
    Clock, MapPin, Bell, BellOff, Gamepad2, Sword, Sparkles
} from 'lucide-react';

const GameCard = ({ game, showActions = true, onSetReminder }) => {
    const gameTypeIcons = {
        mtg: <Sparkles className="w-4 h-4" />,
        dnd: <Sword className="w-4 h-4" />,
        board_game: <Gamepad2 className="w-4 h-4" />
    };

    const statusColors = {
        upcoming: 'bg-[#00ff41]',
        active: 'bg-yellow-500',
        completed: 'bg-gray-500',
        cancelled: 'bg-red-500'
    };

    return (
        <div className="terminal-window p-4 card-hover">
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="text-[#00ff41]">
                        {gameTypeIcons[game.game_type] || <Gamepad2 className="w-4 h-4" />}
                    </span>
                    <h3 className="text-lg font-bold text-[#00ff41]">{game.title}</h3>
                </div>
                <span className={`px-2 py-0.5 text-xs uppercase ${statusColors[game.status]} text-black`}>
                    {game.status}
                </span>
            </div>

            <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#008f11]">
                    <Calendar className="w-3 h-3" />
                    <span>{format(new Date(game.date_time), 'PPP p')}</span>
                </div>
                <div className="flex items-center gap-2 text-[#008f11]">
                    <Clock className="w-3 h-3" />
                    <span>{game.duration_hours}h • {game.game_name}</span>
                </div>
                <div className="flex items-center gap-2 text-[#008f11]">
                    <Users className="w-3 h-3" />
                    <span>{game.current_players?.length || 0}/{game.max_players} players</span>
                </div>
                {game.address && (
                    <div className="flex items-center gap-2 text-[#008f11]">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{game.address}</span>
                    </div>
                )}
            </div>

            {showActions && game.status === 'upcoming' && (
                <div className="mt-4 pt-3 border-t border-[#003b00]">
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onSetReminder(game.game_id)}
                        className="w-full border-[#003b00] text-[#00ff41] text-xs"
                    >
                        <Bell className="w-3 h-3 mr-2" />
                        Set Reminder
                    </Button>
                </div>
            )}
        </div>
    );
};

const HistoryPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    const [activeTab, setActiveTab] = useState('games');
    const [myGames, setMyGames] = useState([]);
    const [history, setHistory] = useState([]);
    const [stats, setStats] = useState(null);
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [gamesRes, historyRes, statsRes, remindersRes] = await Promise.all([
                    userAPI.getMyGames(),
                    userAPI.getMyHistory({ limit: 20 }),
                    userAPI.getMyStats(),
                    userAPI.getMyReminders()
                ]);
                
                setMyGames(gamesRes.data || []);
                setHistory(historyRes.data || []);
                setStats(statsRes.data);
                setReminders(remindersRes.data || []);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSetReminder = async (gameId) => {
        try {
            await remindersAPI.createReminder(gameId, 1);
            toast.success('Reminder set for 1 hour before the game!');
            // Refresh reminders
            const res = await userAPI.getMyReminders();
            setReminders(res.data || []);
        } catch (error) {
            console.error('Error setting reminder:', error);
            toast.error('Failed to set reminder');
        }
    };

    const handleDeleteReminder = async (reminderId) => {
        try {
            await remindersAPI.deleteReminder(reminderId);
            toast.success('Reminder cancelled');
            setReminders(reminders.filter(r => r.reminder_id !== reminderId));
        } catch (error) {
            console.error('Error deleting reminder:', error);
            toast.error('Failed to cancel reminder');
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="matrix-loader" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="history-page">
            <div className="scanlines" />
            
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 border-b border-[#003b00] backdrop-blur-sm">
                <div className="container mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                            className="text-[#00ff41]"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        <h1 className="text-xl font-bold text-[#00ff41] tracking-wider flex items-center gap-2">
                            <History className="w-5 h-5" />
                            MY_GAMES
                        </h1>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-20 pb-10 px-4">
                <div className="container mx-auto max-w-4xl">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="matrix-loader" />
                        </div>
                    ) : (
                        <>
                            {/* Stats Overview */}
                            {stats && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8"
                                >
                                    <div className="terminal-window p-4 text-center">
                                        <Trophy className="w-6 h-6 text-[#00ff41] mx-auto mb-2" />
                                        <p className="text-2xl font-bold text-[#00ff41]">{stats.games_hosted}</p>
                                        <p className="text-xs text-[#008f11] uppercase tracking-wider">Hosted</p>
                                    </div>
                                    <div className="terminal-window p-4 text-center">
                                        <Users className="w-6 h-6 text-[#00ff41] mx-auto mb-2" />
                                        <p className="text-2xl font-bold text-[#00ff41]">{stats.games_joined}</p>
                                        <p className="text-xs text-[#008f11] uppercase tracking-wider">Joined</p>
                                    </div>
                                    <div className="terminal-window p-4 text-center">
                                        <Sparkles className="w-6 h-6 text-[#00ff41] mx-auto mb-2" />
                                        <p className="text-2xl font-bold text-[#00ff41]">{stats.games_by_type?.mtg || 0}</p>
                                        <p className="text-xs text-[#008f11] uppercase tracking-wider">MTG</p>
                                    </div>
                                    <div className="terminal-window p-4 text-center">
                                        <Sword className="w-6 h-6 text-[#00ff41] mx-auto mb-2" />
                                        <p className="text-2xl font-bold text-[#00ff41]">{stats.games_by_type?.dnd || 0}</p>
                                        <p className="text-xs text-[#008f11] uppercase tracking-wider">D&D</p>
                                    </div>
                                </motion.div>
                            )}

                            {/* Tabs */}
                            <Tabs value={activeTab} onValueChange={setActiveTab}>
                                <TabsList className="bg-[#0a0a0a] border border-[#003b00] mb-6">
                                    <TabsTrigger 
                                        value="games"
                                        className="data-[state=active]:bg-[#00ff41] data-[state=active]:text-black"
                                    >
                                        My Games
                                    </TabsTrigger>
                                    <TabsTrigger 
                                        value="reminders"
                                        className="data-[state=active]:bg-[#00ff41] data-[state=active]:text-black"
                                    >
                                        Reminders ({reminders.length})
                                    </TabsTrigger>
                                    <TabsTrigger 
                                        value="history"
                                        className="data-[state=active]:bg-[#00ff41] data-[state=active]:text-black"
                                    >
                                        History
                                    </TabsTrigger>
                                </TabsList>

                                {/* My Games Tab */}
                                <TabsContent value="games">
                                    {myGames.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {myGames.map((game) => (
                                                <GameCard 
                                                    key={game.game_id} 
                                                    game={game}
                                                    onSetReminder={handleSetReminder}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-16 terminal-window">
                                            <Gamepad2 className="w-12 h-12 text-[#003b00] mx-auto mb-4" />
                                            <p className="text-[#008f11] font-mono">
                                                {'>'} No games yet. Join or host one!
                                            </p>
                                            <Button
                                                onClick={() => navigate('/dashboard')}
                                                className="mt-4 bg-[#00ff41] text-black"
                                            >
                                                Find Games
                                            </Button>
                                        </div>
                                    )}
                                </TabsContent>

                                {/* Reminders Tab */}
                                <TabsContent value="reminders">
                                    {reminders.length > 0 ? (
                                        <div className="space-y-4">
                                            {reminders.map((reminder) => (
                                                <div 
                                                    key={reminder.reminder_id}
                                                    className="terminal-window p-4 flex items-center justify-between"
                                                >
                                                    <div>
                                                        <h3 className="text-[#00ff41] font-bold">
                                                            {reminder.game?.title || 'Game'}
                                                        </h3>
                                                        <p className="text-[#008f11] text-sm">
                                                            Reminder: {format(new Date(reminder.remind_at), 'PPP p')}
                                                        </p>
                                                        <p className="text-[#008f11] text-xs">
                                                            Game starts: {reminder.game && format(new Date(reminder.game.date_time), 'PPP p')}
                                                        </p>
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDeleteReminder(reminder.reminder_id)}
                                                        className="text-[#ff0033]"
                                                    >
                                                        <BellOff className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-16 terminal-window">
                                            <Bell className="w-12 h-12 text-[#003b00] mx-auto mb-4" />
                                            <p className="text-[#008f11] font-mono">
                                                {'>'} No reminders set
                                            </p>
                                        </div>
                                    )}
                                </TabsContent>

                                {/* History Tab */}
                                <TabsContent value="history">
                                    {history.length > 0 ? (
                                        <div className="space-y-2">
                                            {history.map((entry) => (
                                                <div 
                                                    key={entry.history_id}
                                                    className="flex items-center gap-4 p-3 bg-[#0a0a0a] border border-[#003b00]"
                                                >
                                                    <div className={`w-2 h-2 rounded-full ${
                                                        entry.action === 'hosted' ? 'bg-[#00ff41]' :
                                                        entry.action === 'joined' ? 'bg-blue-500' :
                                                        entry.action === 'left' ? 'bg-yellow-500' :
                                                        'bg-gray-500'
                                                    }`} />
                                                    <div className="flex-1">
                                                        <p className="text-[#00ff41] text-sm">
                                                            <span className="uppercase text-[#008f11]">{entry.action}</span>
                                                            {' '}{entry.game?.title || 'a game'}
                                                        </p>
                                                        <p className="text-[#008f11] text-xs">
                                                            {format(new Date(entry.created_at), 'PPP p')}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-16 terminal-window">
                                            <History className="w-12 h-12 text-[#003b00] mx-auto mb-4" />
                                            <p className="text-[#008f11] font-mono">
                                                {'>'} No history yet
                                            </p>
                                        </div>
                                    )}
                                </TabsContent>
                            </Tabs>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default HistoryPage;
